import { Check, Search, X } from '@griply/assets';
import { getAutoCompleteSuggestions } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelectTrigger } from 'shared/components/ui/form-select-trigger';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuNoResults,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { ButtonVariants } from 'shared/components/ui/popup-menu/types';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';
import { useTheme } from 'styled-components';

import * as Styled from './custom-form-select.style';
import { DisplayTypes, Option, Size } from './types';

const defaultRenderOption: React.FC<{ value: Option }> = ({ value }) =>
  value.label;

export type CustomFormSelectProps = {
  id?: ID;
  options: Option[];
  // an array checks several options at once; the trigger still shows the first
  value?: string | string[];
  placeholder?: string;
  selectedLabel?: string;
  interActionOption?: Option;
  onInteractionOption?: () => void;
  onChange?: (value?: string) => void;
  startIconColor?: Colors;
  endColor?: Colors;
  canClear?: boolean;
  renderValue?: React.FC<{ value: Option }>;
  noBorders?: boolean;
  size?: Size;
  inheritColor?: boolean;
  noOverflow?: boolean;
  disabled?: boolean;
  // presence turns the dropdown into a searchable listbox — the long option lists
  // (time zones) are unusable without it
  searchPlaceholder?: string;
};

export const CustomFormSelect = forwardRef<
  HTMLDivElement,
  CustomFormSelectProps
>(
  (
    {
      id,
      options,
      value,
      placeholder,
      selectedLabel,
      onChange,
      interActionOption,
      onInteractionOption,
      startIconColor,
      endColor,
      canClear,
      renderValue: RenderValue = defaultRenderOption,
      noBorders,
      size = Size.Medium,
      inheritColor,
      noOverflow,
      disabled,
      searchPlaceholder,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const containerRef = useForwardedRef<HTMLDivElement>(ref);
    const menuRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();
    const { menuOpen, openMenu, closeMenu } = useOpenMenu();
    const [search, setSearch] = useState('');

    const isSearchable = !!searchPlaceholder;

    const onOpen = () => {
      setSearch('');
      openMenu();
    };

    const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
      setSearch(event.target.value);

    const matchedOptions = useMemo(
      () =>
        isSearchable
          ? getAutoCompleteSuggestions(search, options, [
              'label',
              'description',
              'searchTerms',
            ])
          : options,
      [isSearchable, options, search],
    );

    const optionsLists = useMemo(
      () =>
        matchedOptions.reduce<Option[][]>(
          (acc, option) => {
            acc[acc.length - 1].push(option);

            if (option.hasBottomSeparator) {
              acc.push([]);
            }

            return acc;
          },
          [[]],
        ),
      [matchedOptions],
    );

    const _onChange = (e: React.MouseEvent) => {
      const id = e.currentTarget?.getAttribute('data-value');
      if (id) {
        onChange?.(id);
      }

      closeMenu();
      triggerRef.current?.focus();
    };

    const onClear = (e: React.MouseEvent) => {
      e.preventDefault();
      onChange?.(undefined);
      closeMenu();
      triggerRef.current?.focus();
    };

    const selectedValues = useMemo(
      () => (Array.isArray(value) ? value : value ? [value] : []),
      [value],
    );

    const selectedOption = useMemo(
      () => options.find((option) => option.value === selectedValues[0]),
      [options, selectedValues],
    );
    const hasSelectedValue = !!selectedOption || !!selectedLabel;

    useEffect(() => {
      const container = menuRef.current;
      if (!menuOpen || !container) {
        return;
      }

      const getOptions = () =>
        Array.from(
          container.querySelectorAll<HTMLButtonElement>('button[data-value]'),
        );

      // a searchable menu opens on its input instead — PopupMenu focuses it once positioned
      if (!isSearchable) {
        const optionButtons = getOptions();
        const selected = optionButtons.find(
          (option) => option.getAttribute('data-value') === selectedValues[0],
        );
        (selected ?? optionButtons[0])?.focus();
      }

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
          return;
        }
        event.preventDefault();

        const currentOptions = getOptions();
        if (!currentOptions.length) {
          return;
        }

        const currentIndex = currentOptions.findIndex(
          (option) => option === document.activeElement,
        );
        // focus sits outside the list (the search input) — enter it from the matching end
        if (currentIndex === -1) {
          const entry =
            event.key === 'ArrowDown'
              ? currentOptions[0]
              : currentOptions[currentOptions.length - 1];
          entry?.focus();
          return;
        }

        const nextIndex =
          event.key === 'ArrowDown'
            ? (currentIndex + 1) % currentOptions.length
            : (currentIndex - 1 + currentOptions.length) %
              currentOptions.length;
        currentOptions[nextIndex]?.focus();
      };

      container.addEventListener('keydown', onKeyDown);
      return () => container.removeEventListener('keydown', onKeyDown);
    }, [isSearchable, menuOpen, selectedValues]);

    return (
      <Styled.InputContainer ref={containerRef}>
        <FormSelectTrigger
          id={id}
          onClick={onOpen}
          isPlaceholder={!hasSelectedValue}
          noBorders={noBorders}
          size={size}
          inheritColor={inheritColor}
          noOverflow={noOverflow}
          disabled={disabled}
          ariaHasPopup="listbox"
          ariaExpanded={menuOpen}
          ariaControls={menuOpen ? listboxId : undefined}
          endAdornment={
            hasSelectedValue && canClear && !disabled ? (
              <Styled.ClearButton onClick={onClear} type="button" tabIndex={-1}>
                <Icon
                  icon={X}
                  size={16}
                  strokeWidth={2}
                  color={theme.palette.grey[600]}
                />
              </Styled.ClearButton>
            ) : undefined
          }
        >
          {selectedLabel ??
            (selectedOption ? (
              <RenderValue value={selectedOption} />
            ) : (
              placeholder
            ))}
        </FormSelectTrigger>

        {menuOpen && (
          <PopupMenu
            ref={menuRef}
            referenceElement={containerRef}
            onOutside={closeMenu}
            position="bottom"
            fallbackPositions={[
              'top',
              'right-end',
              'right-start',
              'bottom-end',
              'bottom-start',
              'left-start',
              'left-end',
              'top-end',
              'top-start',
            ]}
            hasParentWidth
            initialFocus={isSearchable ? searchRef : undefined}
          >
            {isSearchable && (
              <PopupMenuList hasBottomBorder>
                <PopupMenuListItem>
                  <PopupMenuInput
                    ref={searchRef}
                    startIcon={Search}
                    value={search}
                    placeholder={searchPlaceholder}
                    onChange={onChangeSearch}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            )}

            {optionsLists.map((optionsList, index) => {
              const groupTitle = optionsList[0]?.groupTitle;

              return (
                <React.Fragment key={`options-${index}`}>
                  {!!groupTitle && (
                    <PopupMenuTitle title={groupTitle} isSubTitle />
                  )}
                  <PopupMenuList
                    id={index === 0 ? listboxId : undefined}
                    role="listbox"
                    hasBottomBorder={index !== optionsLists.length - 1}
                  >
                    {optionsList.map((option) => {
                      const isSelected = selectedValues.includes(option.value);

                      return (
                        <PopupMenuListItem
                          key={option.value}
                          role="presentation"
                        >
                          <PopupMenuButton
                            start={option.start}
                            startColor={startIconColor}
                            onClick={_onChange}
                            data-value={option.value}
                            role="option"
                            aria-selected={isSelected}
                            isSelected={isSelected}
                            end={
                              isSelected || !!option.description ? (
                                <Styled.EndContent>
                                  {!!option.description && (
                                    <Typography variant="overline">
                                      {option.description}
                                    </Typography>
                                  )}
                                  {isSelected && (
                                    <Icon icon={Check} strokeWidth={2.5} />
                                  )}
                                </Styled.EndContent>
                              ) : undefined
                            }
                            endColor={endColor}
                            variant={
                              option.displayType === DisplayTypes.ErrorButton
                                ? ButtonVariants.Error
                                : undefined
                            }
                          >
                            {option.label}
                          </PopupMenuButton>
                        </PopupMenuListItem>
                      );
                    })}
                  </PopupMenuList>
                </React.Fragment>
              );
            })}

            {isSearchable && !matchedOptions.length && (
              <PopupMenuNoResults>
                {t('general.labels.no-results')}
              </PopupMenuNoResults>
            )}

            {!!interActionOption && (
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuButton
                    start={interActionOption.start}
                    end={
                      <Typography variant="overline">
                        {interActionOption.description}
                      </Typography>
                    }
                    onClick={onInteractionOption}
                  >
                    {interActionOption.label}
                  </PopupMenuButton>
                </PopupMenuListItem>
              </PopupMenuList>
            )}
          </PopupMenu>
        )}
      </Styled.InputContainer>
    );
  },
);
